import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Activity,
  TrendingUp,
  Users,
  Mail,
  CheckCircle,
  AlertCircle,
  Settings,
  Play,
  Pause,
  RefreshCw,
  Clock,
  BarChart3,
  TestTube,
  Edit2,
  Save,
  Eye,
  EyeOff,
  Target,
  Zap,
  Calendar
} from 'lucide-react';
import { api } from '../../../utils/api';
import toast from 'react-hot-toast';

const SmartNotificationControl = ({ t }) => {
  // Detect dark mode from document classList
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDark(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);
  const [settings, setSettings] = useState([]);
  const [stats, setStats] = useState({
    totalNotifications: 0,
    activeRules: 0,
    criticalNotifications: 0,
    basicNotifications: 0,
    byEntity: {}
  });
  const [taskStatus, setTaskStatus] = useState({
    enabled: false,
    lastRun: null,
    interval: 3600000,
    nextRun: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  // Entity type icons and colors
  const entityConfig = {
    Customer: { icon: Users, color: 'blue', label: 'Customers' },
    Lead: { icon: Target, color: 'green', label: 'Leads' },
    Opportunity: { icon: TrendingUp, color: 'purple', label: 'Opportunities' },
    Activity: { icon: Calendar, color: 'orange', label: 'Activities' },
    Knowledge: { icon: Bell, color: 'indigo', label: 'Knowledge' }
  };

  useEffect(() => {
    loadNotificationData();
  }, []);

  const loadNotificationData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchNotificationSettings(),
        fetchTaskStatus(),
        fetchNotificationStats()
      ]);
    } catch (error) {
      toast.error('Failed to load notification data');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const response = await api.get('/notifications/admin/settings');
      if (response.data && (response.data.success || Array.isArray(response.data))) {
        const settingsData = Array.isArray(response.data) ? response.data : (response.data.data || []);
        setSettings(settingsData);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      throw error;
    }
  };

  const fetchTaskStatus = async () => {
    try {
      const response = await api.get('/notifications/admin/task-status');
      if (response.data?.success) {
        setTaskStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching task status:', error);
    }
  };

  const fetchNotificationStats = async () => {
    try {
      const response = await api.get('/notifications/count');
      if (response.data?.success) {
        setStats({
          totalNotifications: response.data.data.total || 0,
          activeRules: settings.filter(s => s.IsEnabled).length,
          criticalNotifications: response.data.data.critical || 0,
          basicNotifications: response.data.data.basic || 0,
          byEntity: response.data.data.byEntity || {}
        });
      }
    } catch (error) {
      console.error('Error fetching notification stats:', error);
    }
  };

  const toggleTask = async () => {
    setSaving(true);
    try {
      const newStatus = !taskStatus.enabled;
      const response = await api.post('/notifications/admin/task-toggle', {
        enabled: newStatus
      });
      
      if (response.data?.success) {
        setTaskStatus(prev => ({ ...prev, enabled: newStatus }));
        toast.success(`Notification system ${newStatus ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      toast.error('Failed to toggle notification system');
    } finally {
      setSaving(false);
    }
  };

  const updateTaskInterval = async (interval) => {
    setSaving(true);
    try {
      const response = await api.put('/notifications/admin/task-interval', { interval });
      if (response.data?.success) {
        setTaskStatus(prev => ({ ...prev, interval }));
        toast.success(`Check interval updated to ${Math.round(interval / 60000)} minutes`);
      }
    } catch (error) {
      toast.error('Failed to update check interval');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (setting) => {
    setEditingId(setting.SettingID);
    setEditingData({
      isEnabled: setting.IsEnabled,
      emailEnabled: !!setting.EmailEnabled,
      salesBuddyEnabled: !!setting.SalesBuddyEnabled,
      title: setting.Title,
      messageTemplate: setting.MessageTemplate,
      targetRoles: setting.TargetRoles ? JSON.parse(setting.TargetRoles) : [],
      dueDateOffset: setting.DueDateOffset || 0
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingData({});
  };

  const saveSetting = async (settingId) => {
    setSaving(true);
    try {
      const response = await api.put(`/notifications/admin/settings/${settingId}`, editingData);
      if (response.data?.success || response.status === 200) {
        toast.success('Notification rule updated successfully');
        setEditingId(null);
        setEditingData({});
        await fetchNotificationSettings();
      }
    } catch (error) {
      toast.error('Failed to update notification rule');
    } finally {
      setSaving(false);
    }
  };

  const testNotification = async (setting) => {
    setSaving(true);
    try {
      const response = await api.post('/notifications/admin/test', {
        entityType: setting.EntityType,
        eventType: setting.EventType
      });
      
      if (response.data?.success || response.status === 200) {
        toast.success(`Test notification sent for ${setting.EntityType} ${setting.EventType}`);
      }
    } catch (error) {
      toast.error('Failed to send test notification');
    } finally {
      setSaving(false);
    }
  };

  const triggerActivityCheck = async () => {
    setSaving(true);
    try {
      const response = await api.post('/notifications/admin/check-activities');
      if (response.data?.success || response.status === 200) {
        toast.success('Activity due date check completed');
      }
    } catch (error) {
      toast.error('Failed to check activity due dates');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleToggle = (role) => {
    const currentRoles = editingData.targetRoles || [];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    setEditingData(prev => ({ ...prev, targetRoles: newRoles }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-600 dark:text-blue-400" />
          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading notification system...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
            <Zap className={`h-6 w-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          </div>
          <div>
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Smart Notification Control
            </h2>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Manage real-time notification rules and system settings
            </p>
          </div>
        </div>
        <button
          onClick={loadNotificationData}
          disabled={loading}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
            isDark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          } disabled:opacity-50`}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">Refresh</span>
        </button>
      </div>

      {/* Tabs */}
      <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'rules', label: 'Notification Rules', icon: Settings },
            { id: 'system', label: 'System Control', icon: Activity }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? `${isDark ? 'border-blue-400 text-blue-400' : 'border-blue-600 text-blue-600'}`
                    : `${isDark ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Rules</p>
                  <p className={`text-3xl font-bold mt-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{settings.length}</p>
                </div>
                <div className={`p-3 rounded-full ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                  <Bell className={`h-6 w-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
              </div>
              <div className="mt-4">
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {settings.filter(s => s.IsEnabled).length} active
                </span>
              </div>
            </div>

            <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>System Status</p>
                  <p className={`text-3xl font-bold mt-2 ${taskStatus.enabled ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {taskStatus.enabled ? 'Active' : 'Stopped'}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${taskStatus.enabled ? (isDark ? 'bg-green-900/30' : 'bg-green-100') : (isDark ? 'bg-red-900/30' : 'bg-red-100')}`}>
                  <Activity className={`h-6 w-6 ${taskStatus.enabled ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}`} />
                </div>
              </div>
              <div className="mt-4">
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Check interval: {Math.round(taskStatus.interval / 60000)}min
                </span>
              </div>
            </div>

            <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Critical Alerts</p>
                  <p className={`text-3xl font-bold mt-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.criticalNotifications}</p>
                </div>
                <div className={`p-3 rounded-full ${isDark ? 'bg-red-900/30' : 'bg-red-100'}`}>
                  <AlertCircle className={`h-6 w-6 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                </div>
              </div>
              <div className="mt-4">
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Full alert notifications
                </span>
              </div>
            </div>

            <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Basic Badges</p>
                  <p className={`text-3xl font-bold mt-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{stats.basicNotifications}</p>
                </div>
                <div className={`p-3 rounded-full ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                  <CheckCircle className={`h-6 w-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
              </div>
              <div className="mt-4">
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Badge count notifications
                </span>
              </div>
            </div>
          </div>

          {/* Entity Breakdown */}
          <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Notifications by Entity
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {Object.entries(entityConfig).map(([entity, config]) => {
                const Icon = config.icon;
                const count = stats.byEntity[entity.toLowerCase()] || 0;
                return (
                  <div key={entity} className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg bg-${config.color}-100 dark:bg-${config.color}-900/30`}>
                        <Icon className={`h-5 w-5 text-${config.color}-600 dark:text-${config.color}-400`} />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{config.label}</p>
                        <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{count}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Quick Actions
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <button
                onClick={toggleTask}
                disabled={saving}
                className={`flex items-center justify-center space-x-2 p-4 rounded-lg transition-colors ${
                  taskStatus.enabled
                    ? (isDark ? 'bg-red-900/30 hover:bg-red-900/40 text-red-400' : 'bg-red-100 hover:bg-red-200 text-red-700')
                    : (isDark ? 'bg-green-900/30 hover:bg-green-900/40 text-green-400' : 'bg-green-100 hover:bg-green-200 text-green-700')
                } disabled:opacity-50`}
              >
                {taskStatus.enabled ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                <span className="font-medium">{taskStatus.enabled ? 'Stop System' : 'Start System'}</span>
              </button>

              <button
                onClick={triggerActivityCheck}
                disabled={saving}
                className={`flex items-center justify-center space-x-2 p-4 rounded-lg transition-colors ${
                  isDark 
                    ? 'bg-blue-900/30 hover:bg-blue-900/40 text-blue-400' 
                    : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                } disabled:opacity-50`}
              >
                <Clock className="h-5 w-5" />
                <span className="font-medium">Check Due Dates</span>
              </button>

              <button
                onClick={() => setActiveTab('rules')}
                className={`flex items-center justify-center space-x-2 p-4 rounded-lg transition-colors ${
                  isDark 
                    ? 'bg-purple-900/30 hover:bg-purple-900/40 text-purple-400' 
                    : 'bg-purple-100 hover:bg-purple-200 text-purple-700'
                }`}
              >
                <Settings className="h-5 w-5" />
                <span className="font-medium">Manage Rules</span>
              </button>
            </div>
          </div>

          {/* System Information */}
          <div className={`p-6 rounded-lg ${isDark ? 'bg-blue-900/20 border border-blue-700/50' : 'bg-blue-50 border border-blue-200'}`}>
            <div className="flex items-start space-x-3">
              <AlertCircle className={`h-5 w-5 mt-0.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <div className="flex-1">
                <h4 className={`text-sm font-semibold mb-2 ${isDark ? 'text-blue-300' : 'text-blue-900'}`}>
                  Notification System Information
                </h4>
                <div className={`text-sm space-y-1 ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
                  <p><strong>Basic Notifications:</strong> Show as badge counts on navigation menu items</p>
                  <p><strong>Critical Notifications:</strong> Display as full alert messages at the top of the screen</p>
                  <p><strong>Real-time Delivery:</strong> All notifications are sent instantly via WebSocket connections</p>
                  <p><strong>Activity Monitoring:</strong> System automatically checks for due dates and overdue activities</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rules Tab */}
      {activeTab === 'rules' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Configure notification rules for different entity types and events
            </p>
          </div>

          {settings.map((setting) => {
            const isEditing = editingId === setting.SettingID;
            const data = isEditing ? editingData : {
              isEnabled: setting.IsEnabled,
              emailEnabled: !!setting.EmailEnabled,
              salesBuddyEnabled: !!setting.SalesBuddyEnabled,
              title: setting.Title,
              messageTemplate: setting.MessageTemplate,
              targetRoles: setting.TargetRoles ? JSON.parse(setting.TargetRoles) : [],
              dueDateOffset: setting.DueDateOffset || 0
            };
            const entityInfo = entityConfig[setting.EntityType] || { icon: Bell, color: 'gray', label: setting.EntityType };
            const EntityIcon = entityInfo.icon;

            return (
              <div
                key={setting.SettingID}
                className={`p-6 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className={`p-3 rounded-lg bg-${entityInfo.color}-100 dark:bg-${entityInfo.color}-900/30`}>
                      <EntityIcon className={`h-6 w-6 text-${entityInfo.color}-600 dark:text-${entityInfo.color}-400`} />
                    </div>
                    
                    <div className="flex-1 space-y-4">
                      {/* Header */}
                      <div className="flex items-center space-x-3">
                        <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {setting.EntityType} - {setting.EventType}
                        </h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          setting.NotificationLevel === 'Critical'
                            ? (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800')
                            : (isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800')
                        }`}>
                          {setting.NotificationLevel}
                        </span>
                        <button
                          onClick={() => {
                            if (isEditing) {
                              setEditingData(prev => ({ ...prev, isEnabled: !prev.isEnabled }));
                            } else {
                              startEditing(setting);
                              setEditingData(prev => ({ ...prev, isEnabled: !prev.isEnabled }));
                              saveSetting(setting.SettingID);
                            }
                          }}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            data.isEnabled ? 'bg-blue-600' : (isDark ? 'bg-gray-700' : 'bg-gray-200')
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              data.isEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Title */}
                      {isEditing ? (
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Title
                          </label>
                          <input
                            type="text"
                            value={data.title}
                            onChange={(e) => setEditingData(prev => ({ ...prev, title: e.target.value }))}
                            className={`w-full px-3 py-2 rounded-lg border ${
                              isDark 
                                ? 'bg-gray-700 border-gray-600 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>
                      ) : (
                        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{data.title}</p>
                      )}

                      {/* Message Template */}
                      {isEditing && (
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Message Template
                          </label>
                          <textarea
                            value={data.messageTemplate}
                            onChange={(e) => setEditingData(prev => ({ ...prev, messageTemplate: e.target.value }))}
                            rows={2}
                            className={`w-full px-3 py-2 rounded-lg border ${
                              isDark 
                                ? 'bg-gray-700 border-gray-600 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:ring-2 focus:ring-blue-500`}
                          />
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Available: {'{entityName}'}, {'{userName}'}, {'{dueDate}'}, {'{status}'}, {'{priority}'}, {'{customerName}'}
                          </p>
                        </div>
                      )}

                      {/* Channels */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Channels
                        </label>
                        {isEditing ? (
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!data.emailEnabled}
                                onChange={() => setEditingData(prev => ({ ...prev, emailEnabled: !prev.emailEnabled }))}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Email</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!data.salesBuddyEnabled}
                                onChange={() => setEditingData(prev => ({ ...prev, salesBuddyEnabled: !prev.salesBuddyEnabled }))}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>SalesBuddy</span>
                            </label>
                          </div>
                        ) : (
                          <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {(data.emailEnabled || data.salesBuddyEnabled)
                              ? ['Email', 'SalesBuddy'].filter(label =>
                                  (label === 'Email' && data.emailEnabled) ||
                                  (label === 'SalesBuddy' && data.salesBuddyEnabled)
                                ).join(', ')
                              : 'None'}
                          </p>
                        )}
                      </div>

                      {/* Target Roles */}
                      {isEditing && (
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Target Roles
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {['Admin', 'Manager', 'Salesperson'].map(role => (
                              <label key={role} className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={(data.targetRoles || []).includes(role)}
                                  onChange={() => handleRoleToggle(role)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{role}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Due Date Offset */}
                      {isEditing && setting.EntityType === 'Activity' && setting.EventType === 'DueDate' && (
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Days Before Due Date
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="30"
                            value={data.dueDateOffset}
                            onChange={(e) => setEditingData(prev => ({ ...prev, dueDateOffset: parseInt(e.target.value) || 0 }))}
                            className={`w-32 px-3 py-2 rounded-lg border ${
                              isDark 
                                ? 'bg-gray-700 border-gray-600 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:ring-2 focus:ring-blue-500`}
                          />
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Send notification this many days before the activity is due
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 ml-4">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => saveSetting(setting.SettingID)}
                          disabled={saving}
                          className={`p-2 rounded-lg transition-colors ${
                            isDark 
                              ? 'bg-green-900/30 hover:bg-green-900/40 text-green-400' 
                              : 'bg-green-100 hover:bg-green-200 text-green-700'
                          } disabled:opacity-50`}
                          title="Save changes"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className={`p-2 rounded-lg transition-colors ${
                            isDark 
                              ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                          title="Cancel"
                        >
                          <EyeOff className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => testNotification(setting)}
                          disabled={saving}
                          className={`p-2 rounded-lg transition-colors ${
                            isDark 
                              ? 'bg-blue-900/30 hover:bg-blue-900/40 text-blue-400' 
                              : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                          } disabled:opacity-50`}
                          title="Test notification"
                        >
                          <TestTube className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => startEditing(setting)}
                          className={`p-2 rounded-lg transition-colors ${
                            isDark 
                              ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                          title="Edit rule"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* System Control Tab */}
      {activeTab === 'system' && (
        <div className="space-y-6">
          {/* System Status */}
          <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              System Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    System Status:
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                      taskStatus.enabled
                        ? (isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800')
                        : (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800')
                    }`}>
                      {taskStatus.enabled ? 'Running' : 'Stopped'}
                    </span>
                    <button
                      onClick={toggleTask}
                      disabled={saving}
                      className={`flex items-center space-x-1 px-3 py-1 text-sm rounded-lg transition-colors ${
                        taskStatus.enabled
                          ? (isDark ? 'bg-red-900/30 hover:bg-red-900/40 text-red-400' : 'bg-red-100 hover:bg-red-200 text-red-700')
                          : (isDark ? 'bg-green-900/30 hover:bg-green-900/40 text-green-400' : 'bg-green-100 hover:bg-green-200 text-green-700')
                      } disabled:opacity-50`}
                    >
                      {taskStatus.enabled ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                      <span>{taskStatus.enabled ? 'Stop' : 'Start'}</span>
                    </button>
                  </div>
                </div>

                {taskStatus.lastRun && (
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Last Check:
                    </span>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {new Date(taskStatus.lastRun).toLocaleString()}
                    </span>
                  </div>
                )}

                {taskStatus.nextRun && (
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Next Check:
                    </span>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {new Date(taskStatus.nextRun).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Check Interval
                  </label>
                  <select
                    value={Math.round(taskStatus.interval / 60000)}
                    onChange={(e) => updateTaskInterval(parseInt(e.target.value) * 60000)}
                    disabled={saving}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-blue-500 disabled:opacity-50`}
                  >
                    <option value={5}>5 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={120}>2 hours</option>
                    <option value={240}>4 hours</option>
                    <option value={480}>8 hours</option>
                    <option value={1440}>24 hours</option>
                  </select>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    How often the system checks for activity due dates
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Manual Actions */}
          <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Manual Actions
            </h3>
            <div className="space-y-3">
              <button
                onClick={triggerActivityCheck}
                disabled={saving}
                className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors ${
                  isDark 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-gray-50 hover:bg-gray-100'
                } disabled:opacity-50`}
              >
                <div className="flex items-center space-x-3">
                  <Clock className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                  <div className="text-left">
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Check Activity Due Dates
                    </p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Manually trigger a check for upcoming and overdue activities
                    </p>
                  </div>
                </div>
                <Play className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>

              <button
                onClick={loadNotificationData}
                disabled={loading}
                className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors ${
                  isDark 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-gray-50 hover:bg-gray-100'
                } disabled:opacity-50`}
              >
                <div className="flex items-center space-x-3">
                  <RefreshCw className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-600'} ${loading ? 'animate-spin' : ''}`} />
                  <div className="text-left">
                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Refresh All Data
                    </p>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Reload notification settings and statistics
                    </p>
                  </div>
                </div>
                <RefreshCw className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
          </div>

          {/* System Information */}
          <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              System Configuration
            </h3>
            <div className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <p><strong>Database:</strong> NotificationSettings, NotificationLogs, UserNotificationStatus</p>
              <p><strong>Delivery Method:</strong> WebSocket (Socket.IO) for real-time updates</p>
              <p><strong>Supported Entities:</strong> Customer, Lead, Opportunity, Activity, Knowledge</p>
              <p><strong>Event Types:</strong> Created, Updated, DueDate, Overdue</p>
              <p><strong>Notification Levels:</strong> Basic (badge count), Critical (full alert)</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartNotificationControl;

